<template>
    <div class="detail-container">
        <div class="nav-bar">
            <button @click="router.push({ name: 'ProductList' })" class="back-link">
                <span>&larr;</span> Zurück zur Übersicht
            </button>
        </div>

        <div v-if="product" class="detail-layout">
            <div class="image-section">
                                <div class="image-wrap">
                                    <img 
                                        v-if="mainImage"
                                        :src="mainImage" 
                                        onerror="this.onerror=null; this.src='https://placehold.co/600x600/f3f4f6/9ca3af?text=Bild+fehlt'" 
                                        :alt="product.name" 
                                        class="main-image"
                                    />

                                    <button v-if="imagesList.length > 1" class="nav-arrow left" @click="prevImage">‹</button>
                                    <button v-if="imagesList.length > 1" class="nav-arrow right" @click="nextImage">›</button>
                                </div>

                                <div v-if="imagesList.length > 1" class="thumbnails">
                                    <img
                                        v-for="(img, idx) in imagesList"
                                        :key="idx"
                                        :src="img"
                                        @click="mainIndex = idx"
                                        :class="['thumb', { active: idx === mainIndex } ]"
                                        onerror="this.onerror=null; this.src='https://placehold.co/120x120/cccccc/333333?text=?'"
                                        />
                                </div>
            </div>

            <div class="info-section">
                <div class="info-header">
                    <h1 class="title">{{ product.name }}</h1>
                    <div class="price-container">
                        <span class="price">{{ formatCurrency(product.price) }}</span>
                    </div>
                </div>

                <div class="description-area">
                    <h3>Beschreibung</h3>
                    
                    <div class="desc-content">
                        <p class="text">{{ product.description }}</p>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getProductById } from '../services/dbApi';

const route = useRoute();
const router = useRouter();

const product = ref(null);
const loading = ref(true);
const error = ref(null);
const mainIndex = ref(0);

const imagesList = computed(() => {
    if (!product.value) return [];
    if (Array.isArray(product.value.images) && product.value.images.length) return product.value.images;
    if (product.value.image) return [product.value.image];
    return [];
});

const mainImage = computed(() => imagesList.value[mainIndex.value] || imagesList.value[0] || null);

const prevImage = () => {
    if (!imagesList.value.length) return;
    mainIndex.value = (mainIndex.value - 1 + imagesList.value.length) % imagesList.value.length;
};

const nextImage = () => {
    if (!imagesList.value.length) return;
    mainIndex.value = (mainIndex.value + 1) % imagesList.value.length;
};

onMounted(async () => {
    const id = route.params.id;
    try {
        loading.value = true;
        error.value = null;
        product.value = await getProductById(id);
    } catch (e) {
        console.error('Error loading product:', e);
        error.value = e?.message || 'Fehler';
    } finally {
        loading.value = false;
    }
});

onMounted(() => {
    const handler = (e) => {
        if (e.key === 'ArrowLeft') prevImage();
        if (e.key === 'ArrowRight') nextImage();
    };
    window.addEventListener('keydown', handler);
    onUnmounted(() => window.removeEventListener('keydown', handler));
});

const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
};
</script>

<style scoped>
/* Scoped styles for the product detail view */

.detail-container {
    max-width: 1200px;
    margin: 40px auto;
    padding: 0 20px;
    font-family: var(--body-font-family, 'Segoe UI', sans-serif);
}

.nav-bar {
    margin-bottom: 30px;
}

.back-link {
    background: none;
    border: none;
    color: #555;
    font-size: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
}

.back-link:hover {
    color: #3f51b5;
}

.detail-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 40px;
    align-items: start;
}

@media (min-width: 900px) {
    .detail-layout {
        grid-template-columns: 1fr 1fr;
    }
    
    .info-section {
        position: sticky;
        top: 20px; 
    }
}

.image-section {
    background-color: #f9f9f9;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
}

.image-wrap {
    width: 100%;
    max-width: 600px;
    max-height: 600px;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    display: block;
}
.main-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
}
.main-image:hover {
    transform: scale(1.03);
}

.info-header {
    text-align: center;
    margin-bottom: 30px;
    border-bottom: 1px solid #eee;
    padding-bottom: 20px;
}

.title {
    font-size: 2.5rem;
    margin: 0 0 10px 0;
    color: #222;
}

.price {
    font-size: 2rem;
    color: #2e7d32;
    font-weight: bold;
    display: block;
}

.description-area h3 {
    text-transform: uppercase;
    font-size: 0.9rem;
    color: #888;
    margin-bottom: 15px;
    letter-spacing: 1px;
}

.desc-content {
    display: flex;
    flex-direction: column;
    gap: 30px;
}

@media (min-width: 1200px) {
    .desc-content {
        flex-direction: row;
        align-items: flex-start;
    }
}

.text {
    flex: 1;
    line-height: 1.6;
    color: #555;
    font-size: 1.1rem;
}

.thumbnails {
    display: flex;
    gap: 10px;
    margin-top: 15px;
    justify-content: center;
    flex-wrap: wrap;
}
.thumbnails .thumb {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border: 2px solid transparent;
    border-radius: 6px;
    cursor: pointer;
}
.thumbnails .thumb.active {
    border-color: #3f51b5;
}

.image-wrap {
    position: relative;
    display: inline-block;
}
.nav-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0,0,0,0.5);
    color: white;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
}
.nav-arrow.left { left: 10px; }
.nav-arrow.right { right: 10px; }
</style>
